import React, { useContext, useEffect, useState } from "react";
import {
  Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, IconButton, InputLabel, List, ListItem,
  ListItemSecondaryAction, ListItemText, MenuItem, Select, TextField,
  Typography, makeStyles
} from "@material-ui/core";
import { Check, Close, DeleteOutline } from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  section: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  addRow: { display: "flex", gap: 8, alignItems: "flex-end" },
  grow: { flex: 1 },
  collaborator: { marginRight: 4, marginBottom: 4 },
  note: { whiteSpace: "pre-wrap" }
}));

const KanbanTicketDetails = ({ open, onClose, ticket, users, onUpdate }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [note, setNote] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [followUpAt, setFollowUpAt] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");
  const [followUpUserId, setFollowUpUserId] = useState(ticket?.userId || user.id);
  const [priority, setPriority] = useState(ticket?.priority || "normal");
  const [dueAt, setDueAt] = useState(ticket?.dueAt ? new Date(ticket.dueAt).toISOString().slice(0, 16) : "");
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});

  useEffect(() => {
    if (!open || !ticket) return;
    Promise.all([
      api.get("/ticket-custom-fields"),
      api.get(`/tickets/${ticket.id}/custom-fields`)
    ]).then(([fieldsResult, valuesResult]) => {
      setCustomFields(fieldsResult.data.filter(field => field.active));
      setCustomValues(valuesResult.data.reduce((acc, item) => ({ ...acc, [item.fieldId]: item.value }), {}));
      setPriority(ticket.priority || "normal");
      setDueAt(ticket.dueAt ? new Date(ticket.dueAt).toISOString().slice(0, 16) : "");
      setFollowUpUserId(ticket.userId || user.id);
    }).catch(toastError);
  }, [open, ticket?.id]);
  if (!ticket) return null;
  const canManage = user.profile === "admin" || Number(ticket.userId) === Number(user.id);
  const approved = (ticket.collaborators || []).filter(item => item.status === "approved");
  const pending = (ticket.collaborators || []).filter(item => item.status === "pending");

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/notes`, { body: note });
      setNote("");
      onUpdate(data);
    } catch (err) { toastError(err); }
  };

  const deleteNote = async noteId => {
    try {
      await api.delete(`/ticket-notes/${noteId}`);
      onUpdate({ ...ticket, notes: ticket.notes.filter(item => item.id !== noteId) });
    } catch (err) { toastError(err); }
  };

  const share = async () => {
    if (!selectedUserId) return;
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/collaborators`, { userId: selectedUserId });
      setSelectedUserId("");
      onUpdate(data);
    } catch (err) { toastError(err); }
  };

  const decide = async (userId, status) => {
    try {
      const { data } = await api.put(`/tickets/${ticket.id}/access-requests/${userId}`, { status });
      onUpdate(data);
    } catch (err) { toastError(err); }
  };

  const removeCollaborator = async userId => {
    try {
      const { data } = await api.delete(`/tickets/${ticket.id}/collaborators/${userId}`);
      onUpdate(data);
    } catch (err) { toastError(err); }
  };

  const scheduleFollowUp = async () => {
    if (!followUpAt || !followUpNote.trim() || !followUpUserId) return;
    try {
      await api.post(`/tickets/${ticket.id}/follow-ups`, {
        dueAt: new Date(followUpAt).toISOString(),
        note: followUpNote,
        assignedUserId: followUpUserId
      });
      setFollowUpAt("");
      setFollowUpNote("");
    } catch (err) { toastError(err); }
  };

  const saveTicketDetails = async () => {
    try {
      const { data } = await api.put(`/kanban/tickets/${ticket.id}/details`, {
        priority,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null
      });
      onUpdate(data);
    } catch (err) { toastError(err); }
  };

  const saveCustomFields = async () => {
    const missing = customFields.find(field => field.required && !String(customValues[field.id] || "").trim());
    if (missing) return;
    try {
      await api.put(`/tickets/${ticket.id}/custom-fields`, {
        values: customFields.map(field => ({ fieldId: field.id, value: customValues[field.id] || "" }))
      });
    } catch (err) { toastError(err); }
  };

  const fieldOptions = field => {
    try { return JSON.parse(field.options || "[]"); } catch (_) { return []; }
  };

  const renderCustomField = field => {
    const value = customValues[field.id] || "";
    const onChange = nextValue => setCustomValues(current => ({ ...current, [field.id]: nextValue }));
    if (field.fieldType === "select" || field.fieldType === "multiselect") return <FormControl fullWidth margin="dense" key={field.id}>
      <InputLabel>{field.name}{field.required ? " *" : ""}</InputLabel>
      <Select multiple={field.fieldType === "multiselect"} value={field.fieldType === "multiselect" ? (() => { try { return JSON.parse(value || "[]"); } catch (_) { return []; } })() : value} onChange={event => onChange(field.fieldType === "multiselect" ? JSON.stringify(event.target.value) : event.target.value)}>
        {fieldOptions(field).map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
      </Select>
    </FormControl>;
    return <TextField
      key={field.id} fullWidth margin="dense" variant="outlined"
      label={`${field.name}${field.required ? " *" : ""}`}
      type={field.fieldType === "number" ? "number" : field.fieldType === "date" ? "date" : "text"}
      value={value} onChange={event => onChange(event.target.value)}
      InputLabelProps={field.fieldType === "date" ? { shrink: true } : undefined}
    />;
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{i18n.t("kanbanDetails.title")} — {ticket.contact?.name}</DialogTitle>
    <DialogContent dividers>
      <Typography className={classes.section} variant="subtitle1">{i18n.t("kanbanDetails.notes")}</Typography>
      <div className={classes.addRow}>
        <TextField className={classes.grow} multiline rows={2} variant="outlined" value={note} onChange={event => setNote(event.target.value)} placeholder={i18n.t("kanbanDetails.notePlaceholder")} />
        <Button color="primary" variant="contained" onClick={addNote}>{i18n.t("kanbanDetails.add")}</Button>
      </div>
      <List dense>
        {(ticket.notes || []).map(item => <ListItem key={item.id} divider>
          <ListItemText
            primary={<span className={classes.note}>{item.body}</span>}
            secondary={`${item.user?.name || ""} — ${new Date(item.createdAt).toLocaleString("pt-BR")}`}
          />
          {(user.profile === "admin" || Number(item.userId) === Number(user.id)) && <ListItemSecondaryAction>
            <IconButton size="small" onClick={() => deleteNote(item.id)}><DeleteOutline /></IconButton>
          </ListItemSecondaryAction>}
        </ListItem>)}
      </List>
      <Divider />
      <Typography className={classes.section} variant="subtitle1">{i18n.t("kanbanDetails.collaborators")}</Typography>
      <div>{approved.map(item => <Chip
        key={item.userId}
        className={classes.collaborator}
        label={item.user?.name}
        onDelete={canManage ? () => removeCollaborator(item.userId) : undefined}
      />)}</div>
      {canManage && <div className={classes.addRow}>
        <FormControl className={classes.grow}>
          <InputLabel>{i18n.t("kanbanDetails.selectUser")}</InputLabel>
          <Select value={selectedUserId} onChange={event => setSelectedUserId(event.target.value)}>
            {users.filter(item => Number(item.id) !== Number(ticket.userId)).map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button color="primary" onClick={share}>{i18n.t("kanbanDetails.share")}</Button>
      </div>}
      {canManage && pending.length > 0 && <>
        <Typography className={classes.section} variant="subtitle2">{i18n.t("kanbanDetails.requests")}</Typography>
        <List dense>{pending.map(item => <ListItem key={item.userId}>
          <ListItemText primary={item.user?.name} />
          <ListItemSecondaryAction>
            <IconButton size="small" onClick={() => decide(item.userId, "approved")}><Check /></IconButton>
            <IconButton size="small" onClick={() => decide(item.userId, "rejected")}><Close /></IconButton>
          </ListItemSecondaryAction>
        </ListItem>)}</List>
      </>}
      <Divider />
      <Typography className={classes.section} variant="subtitle1">Campos personalizados</Typography>
      {customFields.map(renderCustomField)}
      {customFields.length > 0 && <Button color="primary" variant="contained" onClick={saveCustomFields}>Salvar campos</Button>}
      <Divider />
      <Typography className={classes.section} variant="subtitle1">Agendar retorno</Typography>
      <div className={classes.addRow}>
        <FormControl className={classes.grow}>
          <InputLabel>Prioridade</InputLabel>
          <Select value={priority} onChange={event => setPriority(event.target.value)}>
            <MenuItem value="low">Baixa</MenuItem><MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="high">Alta</MenuItem><MenuItem value="urgent">Urgente</MenuItem>
          </Select>
        </FormControl>
        <TextField type="datetime-local" label="Prazo" value={dueAt} onChange={event => setDueAt(event.target.value)} InputLabelProps={{ shrink: true }} />
        <Button color="primary" onClick={saveTicketDetails}>Salvar</Button>
      </div>
      <TextField
        fullWidth type="datetime-local" variant="outlined" margin="dense"
        value={followUpAt} onChange={event => setFollowUpAt(event.target.value)}
        InputLabelProps={{ shrink: true }} label="Data e horário"
      />
      <TextField
        fullWidth multiline rows={2} variant="outlined" margin="dense"
        value={followUpNote} onChange={event => setFollowUpNote(event.target.value)}
        label="Observação do retorno"
      />
      <FormControl fullWidth margin="dense">
        <InputLabel>Responsável</InputLabel>
        <Select value={followUpUserId} onChange={event => setFollowUpUserId(event.target.value)}>
          {users.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
        </Select>
      </FormControl>
      <Button color="primary" variant="contained" onClick={scheduleFollowUp}>Agendar retorno</Button>
    </DialogContent>
    <DialogActions><Button onClick={onClose}>{i18n.t("kanbanDetails.close")}</Button></DialogActions>
  </Dialog>;
};

export default KanbanTicketDetails;
